"use client";

import {AbsoluteCenter, Menu as ChakraMenu, Icon} from "@chakra-ui/react";
import * as React from "react";
import {LuCheck, LuChevronRight} from "react-icons/lu";
import {Portal} from "../Portal";
import {ReactNode} from "react";

interface MenuContentProps extends ChakraMenu.ContentProps {
  portalled?: boolean;
  portalRef?: React.RefObject<HTMLElement>;
}

export const MenuContent = React.forwardRef<HTMLDivElement, MenuContentProps>(function MenuContent(props, ref) {
  const {portalled = true, portalRef, ...rest} = props;

  // Do not pass `childContainer` to `Portal` because we don't want to nest any elements
  // within the menu portal.
  return (
    <Portal disabled={!portalled} container={portalRef}>
      <ChakraMenu.Positioner>
        <ChakraMenu.Content ref={ref} {...rest} />
      </ChakraMenu.Positioner>
    </Portal>
  );
});

export const MenuArrow = React.forwardRef<HTMLDivElement, ChakraMenu.ArrowProps>(function MenuArrow(props, ref) {
  return (
    <ChakraMenu.Arrow ref={ref} {...props}>
      <ChakraMenu.ArrowTip />
    </ChakraMenu.Arrow>
  );
});

export const MenuCheckboxItem = React.forwardRef<HTMLDivElement, ChakraMenu.CheckboxItemProps>(
  function MenuCheckboxItem(props, ref) {
    return (
      <ChakraMenu.CheckboxItem ref={ref} {...props}>
        <ChakraMenu.ItemIndicator hidden={false} visibility="hidden" _checked={{visibility: "visible"}}>
          <LuCheck />
        </ChakraMenu.ItemIndicator>
        {props.children}
      </ChakraMenu.CheckboxItem>
    );
  },
);

export interface MenuRadioItemProps extends ChakraMenu.RadioItemProps {
  /** Marks the selected item with a check. */
  indicator?: boolean;
}

export const MenuRadioItem = React.forwardRef<HTMLDivElement, MenuRadioItemProps>(function MenuRadioItem(props, ref) {
  const {children, indicator = true, ...rest} = props;
  return (
    <ChakraMenu.RadioItem ps="8" ref={ref} {...rest}>
      {indicator && (
        <AbsoluteCenter axis="horizontal" left="4" asChild>
          <ChakraMenu.ItemIndicator>
            <LuCheck />
          </ChakraMenu.ItemIndicator>
        </AbsoluteCenter>
      )}
      <ChakraMenu.ItemText>{children}</ChakraMenu.ItemText>
    </ChakraMenu.RadioItem>
  );
});

export const MenuItemGroup = React.forwardRef<
  HTMLDivElement,
  Omit<ChakraMenu.ItemGroupProps, "title"> & {title?: ReactNode}
>(function MenuItemGroup(props, ref) {
  const {title, children, ...rest} = props;
  return (
    <ChakraMenu.ItemGroup ref={ref} {...rest}>
      {title && <ChakraMenu.ItemGroupLabel userSelect="none">{title}</ChakraMenu.ItemGroupLabel>}
      {children}
    </ChakraMenu.ItemGroup>
  );
});

export interface MenuTriggerItemProps extends ChakraMenu.TriggerItemProps {
  startIcon?: React.ReactNode;
}

export const MenuTriggerItem = React.forwardRef<HTMLDivElement, MenuTriggerItemProps>(
  function MenuTriggerItem(props, ref) {
    const {startIcon, children, ...rest} = props;
    return (
      <ChakraMenu.TriggerItem ref={ref} {...rest}>
        {startIcon}
        {children}
        <Icon asChild ml="auto" color="gray.500">
          <LuChevronRight />
        </Icon>
      </ChakraMenu.TriggerItem>
    );
  },
);

export const MenuRadioItemGroup = ChakraMenu.RadioItemGroup;
export const MenuContextTrigger = ChakraMenu.ContextTrigger;
export const MenuRoot = ChakraMenu.Root;
export const MenuSeparator = ChakraMenu.Separator;

function swallowClick(event: React.MouseEvent<HTMLDivElement>) {
  event.preventDefault();
  event.stopPropagation();
}

/**
 * Disabling an item greys it out but leaves it clickable, so a caller's `onClick` — or the
 * navigation of an `asChild` link — would still fire; the click is swallowed here instead.
 * Pointer events stay enabled so a tooltip can still explain why the item is disabled.
 */
export const MenuItem = React.forwardRef<HTMLDivElement, ChakraMenu.ItemProps>(function MenuItem(props, ref) {
  const {onClick, ...rest} = props;
  return (
    <ChakraMenu.Item
      ref={ref}
      onClick={props.disabled ? undefined : onClick}
      onClickCapture={props.disabled ? swallowClick : undefined}
      {...rest}
    />
  );
});

export const MenuItemText = ChakraMenu.ItemText;
export const MenuItemGroupLabel = ChakraMenu.ItemGroupLabel;
export const MenuItemCommand = ChakraMenu.ItemCommand;
export const MenuTrigger = ChakraMenu.Trigger;
